import { Job } from "@core/domains/jobs";
import { singleline } from "@core/foundation/string";
import {
  CursorPaginationSchema,
  api,
  apiEndpoint,
  cursorPaginatedResponse,
} from "@core/ports/http";
import { z } from "@core/zod";

import { API_DOMAIN } from "@platform/core";

import { MEMORY_SERVICE } from "../config/index.ts";
import {
  GithubComment,
  GithubRepo,
  Memory,
  MemoryApiKeySchema,
  MemoryIdSchema,
  Project,
  ProjectIdSchema,
  Revision,
  Trigger,
} from "../models/index.ts";

const GithubRepoIdSchema = z.int().nonnegative();

export const memoryApi = api(
  {
    domain: API_DOMAIN,
    path: `${MEMORY_SERVICE.LABEL}/api`,
    scopes: ["memory"],
  },
  {
    addGithubRepoToProject: apiEndpoint({
      body: z.object(
        {
          githubAccountId: z.int().nonnegative(),
          githubRepoId: GithubRepoIdSchema,
          projectId: ProjectIdSchema,
        },
        {
          githubAccountId:
            "The id of the GitHub account whose installation reaches the repo",
          githubRepoId: "The GitHub id of the repo to connect",
          projectId: "The id of the project to connect the repo to",
        },
      ),
      response: z.object(
        { project: Project.schema },
        { project: "The project, with the repo connected" },
      ),
    }),
    archiveMemory: apiEndpoint({
      body: z.object(
        { memoryId: MemoryIdSchema, projectId: ProjectIdSchema },
        {
          memoryId: "The id of the memory to archive",
          projectId: Memory.descriptions.projectId,
        },
      ),
      response: z.object(
        { memory: Memory.schema },
        { memory: "The memory, archived" },
      ),
    }),
    createProject: apiEndpoint({
      body: Project.createSchema.omit({
        connectedGithubRepoIds: true,
        principalId: true,
      }),
      response: z.object(
        { project: Project.schema },
        { project: "The project created" },
      ),
    }),
    generateMemoryApiKey: apiEndpoint({
      body: z.object(
        { projectId: ProjectIdSchema },
        { projectId: "The id of the project to generate the API key for" },
      ),
      response: z.object(
        {
          memoryApiCredential: z.string(),
          memoryApiKey: MemoryApiKeySchema,
          project: Project.schema,
        },
        {
          memoryApiCredential: singleline`
            The credential carrying the key with its project and principal ids,
            returned once
          `,
          memoryApiKey: "The raw API key, returned once",
          project: "The project, holding the new key's digest",
        },
      ),
    }),
    getAllProjects: apiEndpoint({
      response: z.object(
        { items: z.array(Project.schema) },
        { items: "The signed-in user's projects" },
      ),
    }),
    getGithubComments: apiEndpoint({
      body: z.object(
        { ...CursorPaginationSchema.shape, githubRepoId: GithubRepoIdSchema },
        {
          ...CursorPaginationSchema.descriptions,
          githubRepoId: "The GitHub id of the repo whose comments to list",
        },
      ),
      response: cursorPaginatedResponse(GithubComment.schema),
    }),
    getMemories: apiEndpoint({
      body: z.object(
        { ...CursorPaginationSchema.shape, projectId: ProjectIdSchema },
        {
          ...CursorPaginationSchema.descriptions,
          projectId: "The id of the project whose memories to list",
        },
      ),
      response: cursorPaginatedResponse(
        z.object(
          {
            memory: Memory.schema,
            revision: Revision.schema,
            triggers: z.array(Trigger.schema),
          },
          {
            memory: "The memory",
            revision: "The memory's latest revision",
            triggers: "The triggers that inject the memory",
          },
        ),
      ),
    }),
    getProject: apiEndpoint({
      body: z.object({ id: ProjectIdSchema }, { id: Project.descriptions.id }),
      response: z.object(
        { githubRepos: z.array(GithubRepo.schema), project: Project.schema },
        {
          githubRepos: "The GitHub repos connected to the project",
          project: "The project",
        },
      ),
    }),
    reindexGithubComments: apiEndpoint({
      body: z.object(
        { githubRepoId: GithubRepoIdSchema },
        { githubRepoId: "The GitHub id of the repo to reindex" },
      ),
      response: z.object(
        { job: Job.schema },
        { job: "The job syncing the repo's review comments" },
      ),
    }),
    removeGithubRepoFromProject: apiEndpoint({
      body: z.object(
        { githubRepoId: GithubRepoIdSchema, projectId: ProjectIdSchema },
        {
          githubRepoId: "The GitHub id of the repo to disconnect",
          projectId: "The id of the project to disconnect the repo from",
        },
      ),
      response: z.object(
        { project: Project.schema },
        { project: "The project, with the repo disconnected" },
      ),
    }),
    unarchiveMemory: apiEndpoint({
      body: z.object(
        { memoryId: MemoryIdSchema, projectId: ProjectIdSchema },
        {
          memoryId: "The id of the memory to unarchive",
          projectId: Memory.descriptions.projectId,
        },
      ),
      response: z.object(
        { memory: Memory.schema },
        { memory: "The memory, active again" },
      ),
    }),
  },
);
